import React, { useState, useEffect, useRef, useCallback } from 'react';
import { STORE_LIST_URL, STORE_DETAIL_URL } from '../../utils/urls';
import instance from '../../services/services';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Thumbs, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/thumbs';
import 'swiper/css/navigation';
import { useLanguage } from '../../hooks/useLanguage';

function BranchGuide() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [branchList, setBranchList] = useState([]);
  const [detailData, setDetailData] = useState(null);
  const mapElement = useRef(null);
  const mapInstance = useRef(null);
  const naverKey = process.env.REACT_APP_NAVER_KEY || '';
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // 네이버 지도 스크립트 로드
  const loadNaverMapsScript = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (document.getElementById('naver-maps-script')) {
        if (window.naver && window.naver.maps && window.naver.maps.Service) {
          resolve();
        } else {
          // 기존 스크립트가 있지만 아직 로딩 중일 수 있음
          const interval = setInterval(() => {
            if (window.naver && window.naver.maps && window.naver.maps.Service) {
              clearInterval(interval);
              resolve();
            }
          }, 100);
        }
        return;
      }
  
      if (!naverKey) {
        console.error('네이버 API 키가 설정되지 않았습니다.');
        reject(new Error('Naver API key is not defined'));
        return;
      }
  
      const script = document.createElement('script');
      script.id = 'naver-maps-script';
      script.src = `https://openapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${naverKey}&submodules=geocoder`;
      script.onload = () => {
        const interval = setInterval(() => {
          if (window.naver && window.naver.maps && window.naver.maps.Service) {
            clearInterval(interval);
            resolve();
          }
        }, 100);
      };
      script.onerror = (error) => reject(error);
      document.head.appendChild(script);
    });
  }, [naverKey]);

  // 주소 지오코딩
  const geocodeAddress = useCallback((address) => {
    return new Promise((resolve, reject) => {
      if (!window.naver || !window.naver.maps || !window.naver.maps.Service) {
        reject(new Error('Naver maps service is not available'));
        return;
      }

      window.naver.maps.Service.geocode({ query: address }, (status, response) => {
        if (status !== window.naver.maps.Service.Status.OK) {
          console.error('Geocoding failed for address:', address);
          reject(new Error('Geocoding failed'));
          return;
        }
        
        if (response.v2.addresses && response.v2.addresses.length > 0) {
          const result = response.v2.addresses[0];
          resolve({
            lat: result.y,
            lng: result.x,
            address: result.roadAddress || result.jibunAddress,
          });
        } else {
          reject(new Error('No address found'));
        }
      });
    });
  }, []);

  // 지도 초기화
  const initMap = useCallback(async (address) => {
    if (!address || !mapElement.current) return;

    try {
      await loadNaverMapsScript();

      const fullAddress = address;
      const { lat, lng } = await geocodeAddress(fullAddress); // 위도 경도값
      
      // 마커 관련 변수 - 기존 마커 스타일 유지
      let marker = null;
      
      // 이미 맵 인스턴스가 있으면 재사용
      if (mapInstance.current) {
        // 기존 마커 제거 (있는 경우)
        if (mapInstance.current._marker) {
          mapInstance.current._marker.setMap(null);
        }
        
        // 중심 위치 업데이트
        const center = new window.naver.maps.LatLng(lat, lng);
        mapInstance.current.setCenter(center);
        
        // 새 마커 생성
        marker = new window.naver.maps.Marker({
          position: center,
          map: mapInstance.current
        });
        
        // 마커 참조 저장
        mapInstance.current._marker = marker;
        
        return;
      }

      // 새 지도 인스턴스 생성
      const mapOptions = {
        center: new window.naver.maps.LatLng(lat, lng),
        zoom: 16, // 조금 더 확대해서 보이게 함
        zoomControl: true,
        zoomControlOptions: {
          position: window.naver.maps.Position.TOP_RIGHT
        }
      };

      // 맵 생성
      const map = new window.naver.maps.Map(mapElement.current, mapOptions);
      mapInstance.current = map;

      // 마커 생성 (기본 네이버 지도 마커)
      marker = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(lat, lng),
        map: map
      });
      
      // 마커 참조 저장 (나중에 제거할 수 있도록)
      mapInstance.current._marker = marker;
      
      // 지도 로드 완료 이벤트에서 중앙 위치 다시 확인
      window.naver.maps.Event.once(map, 'init_stylemap', () => {
        map.setCenter(new window.naver.maps.LatLng(lat, lng));
      });
      
    } catch (error) {
      console.error('지도 초기화 실패:', error);
    }
  }, [loadNaverMapsScript, geocodeAddress]);

  const handleTabClick = useCallback(async (value) => {
    setActiveTab(value);
    try {
      const response = await instance.post(STORE_DETAIL_URL, {
        store_seq: value
      });
      setDetailData(response.data.data);
    } catch (error) {
      console.error('지점 상세 정보 가져오기 실패:', error);
    }
  }, []);

  useEffect(() => {
    const fetchTermsList = async () => {
      try {
        const response = await instance.post(STORE_LIST_URL);
        setBranchList(response.data.data);
        if (response.data.data.length > 0) {
          handleTabClick(response.data.data[0].store_seq);
        }
      } catch (error) {
        console.error('지점 목록 가져오기 실패:', error);
      }
    };
    fetchTermsList();
  }, [handleTabClick]);

  // 지점 정보가 변경될 때마다 지도 업데이트
  useEffect(() => {
    if (detailData && detailData.info_address_street) {
      const fullAddress = `${detailData.info_address_street}${detailData.info_address_detail ? ', ' + detailData.info_address_detail : ''}`.trim();
      initMap(fullAddress);
    }
  }, [detailData, initMap]);


    // ✅ Swiper 버튼 재초기화
    useEffect(() => {
      if (thumbsSwiper && thumbsSwiper.params.navigation) {
        thumbsSwiper.params.navigation.prevEl = prevRef.current;
        thumbsSwiper.params.navigation.nextEl = nextRef.current;
        thumbsSwiper.navigation.destroy(); // 기존 설정 제거
        thumbsSwiper.navigation.init();    // 새로 등록
        thumbsSwiper.navigation.update();  // 최신화
      }
    }, [thumbsSwiper]);

  return (
    <section className="about_container branch_section">
      <h3 className="commu_title noline">{t('branch_info')}</h3>
      <div className="branch_info">
        <div className="square_tab">
          <ul className="about_tab_list"> 
            {branchList.map((tab, index) => (
              <li
                key={index}
                className={`tab_item ${activeTab === tab.store_seq ? 'active' : ''}`}
                style={{cursor: 'pointer'}}
                onClick={() => handleTabClick(tab.store_seq)}
              >
                {tab.store_name}
              </li>
            ))}
          </ul>
        </div>

        <div className="branch_detail">
          <div className="detail_item thumbs">
            {detailData && (
              <>
                <Swiper
                  modules={[Thumbs, FreeMode]}
                  thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                  className="branch_slide"
                >
                  {detailData.info_image?.map((imageUrl, index) => (
                    <SwiperSlide key={index}>
                      <img 
                        src={imageUrl} 
                        alt={`지점 이미지 ${index + 1}`} 
                        loading="lazy"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>


                {/* <Swiper
                  modules={[Thumbs, FreeMode]}
                  onSwiper={setThumbsSwiper}
                  className="thumbnail"
                  slidesPerView="auto"   // ✅ 여러 썸네일을 한 줄에 자동 배치
                  spaceBetween={8}       // ✅ 썸네일 간 간격
                  freeMode={true}        // ✅ 자유 스크롤
                  watchSlidesProgress={true}
                  centeredSlides={false} // ✅ 첫 슬라이드 왼쪽 정렬
                  initialSlide={0}       // ✅ 첫 슬라이드부터 시작
                >
                  {detailData.info_image?.map((imageUrl, index) => (
                    <SwiperSlide key={index}>
                      <img 
                        src={imageUrl} 
                        alt={`썸네일 ${index + 1}`} 
                        className="object-cover" 
                        loading="lazy"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper> */}
              <div className="thumbnail_wrap" style={{ position: 'relative' }}>
                {/* 왼쪽 화살표 */}
                <button
                  ref={prevRef}
                  className="thumb_nav thumb_prev"
                  aria-label="이전"
                >
                  ‹
                </button>

                {/* Swiper 본체 */}
                <Swiper
                  modules={[Thumbs, FreeMode, Navigation]}
                  onSwiper={setThumbsSwiper}
                  className="thumbnail"
                  slidesPerView="auto"
                  spaceBetween={8}
                  freeMode={true}
                  watchSlidesProgress={true}
                  centeredSlides={false}
                  initialSlide={0}
                  navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                  }}
                  onBeforeInit={(swiper) => {
                    swiper.params.navigation.prevEl = prevRef.current;
                    swiper.params.navigation.nextEl = nextRef.current;
                  }}
                >
                  {detailData.info_image?.map((imageUrl, index) => (
                    <SwiperSlide key={index}>
                      <img
                        src={imageUrl}
                        alt={`썸네일 ${index + 1}`}
                        className="object-cover"
                        loading="lazy"
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>

                {/* 오른쪽 화살표 */}
                <button
                  ref={nextRef}
                  className="thumb_nav thumb_next"
                  aria-label="다음"
                >
                  ›
                </button>
                
              </div>

              </>
            )}
          </div>

          {detailData && (
            <div className="detail_item text">
              <div className='title_box'>
                <h6>{detailData.info_store_name}</h6>
                <span className='g-90 b1_2'>{detailData.info_descript01}</span>
              </div>
              <ul className='about_info_list'>
                <li>
                  <i className='clock_icon'></i>
                  <p className='b1_2'>영업시간: {detailData?.opening_start_hour === '00' && detailData?.opening_end_hour === '00' ? '24시간' : `${detailData?.opening_start_hour}시 ~ ${detailData?.opening_end_hour}시`}</p>
                </li>
                <li className='t_line'>
                  <i className='location_icon'></i>
                  <p className='b1_2'>
                    주소: {detailData.info_address_street}, {detailData.info_address_detail}
                    <span className='g-70 b2_2'>{detailData.info_subway}</span>
                  </p>
                </li>
                {detailData.info_parking && (
                  <li>
                    <i className='parking_icon'></i>
                    <p className='b1_2'>주차: {detailData.info_parking}</p>
                  </li>
                )}
                <li>
                  <i className='phone_icon'></i>
                  <p className='b1_2'>TEL : {detailData.info_tell01} / {detailData.info_tell02}</p>
                </li>
                <li>
                  <i className='fax_icon'></i>
                  <p className='b1_2'>FAX:  {detailData.info_fax}</p>
                </li>
                <li className='t_line'>
                  <i className='msg_icon'></i>
                  <p className='b1_2'>
                    E-mail: {detailData.info_email}
                    <span className='g-70 b2_2'>{t('electronic_tax_invoice_txt')}</span>
                  </p>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="location_info">
        <h6>{t('find_way')}</h6>
        <div 
          id="map" 
          ref={mapElement} 
          className="map-container"
          style={{ width: '100%', height: '600px', margin: 'auto' }}
        />
      </div>
      
      <style>
        {`
          @media (max-width: 767px) {
            .map-container {
              height: 300px !important;
            }
          }
        `}
      </style>
    </section>
  );
}

export default BranchGuide;